import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, VerifiedBadge } from '@shared/ui';
import Avatar from '@shared/ui/Avatar';
import { NamedIcon } from '@shared/ui/icons';
import { currentLocale } from '@shared/i18n';
import { useDialogFocus } from '@shared/hooks/useDialogFocus';

const CreatorPreviewModal = lazy(() => import('./CreatorPreviewModal'));

// The business entry point: every live creator, searchable and filterable, no ad
// category required (get_creator_directory). Filters and the open card live in the
// URL — a link reproduces the view, and the phone's Back closes the card.

interface DirRow {
  provider_id: string;
  handle: string;
  full_name: string | null;
  avatar_url: string | null;
  followers: number;
  er: number | string | null;
  er_median: number | string | null;
  er_reliable: boolean;
  reel_views: number | null;
  reel_price: number | string | null;
  brands_90d: number | null;
  niches: string[] | null;
  languages: string[] | null;
  verified: boolean;
  total: number | string;
}

const PAGE = 30;

const SIZES = [
  { key: 'xs', min: null, max: 10000, label: '<10K' },
  { key: 's', min: 10000, max: 30000, label: '10–30K' },
  { key: 'm', min: 30000, max: 100000, label: '30–100K' },
  { key: 'l', min: 100000, max: null, label: '100K+' },
] as const;
const LANGS = ['ky', 'ru', 'en'] as const;
const SORTS = ['followers', 'er', 'views', 'price'] as const;
type Sort = (typeof SORTS)[number];

const compact = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const loc = currentLocale();
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString(loc, { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `${(v / 1_000).toLocaleString(loc, { maximumFractionDigits: 1 })}K`;
  return v.toLocaleString(loc);
};

export default function Creators() {
  const { t } = useTranslation('customer');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const q = params.get('q') ?? '';
  const size = params.get('size') ?? '';
  const lang = params.get('lang') ?? '';
  const priced = params.get('price') === '1';
  const sort = (SORTS as readonly string[]).includes(params.get('sort') ?? '') ? (params.get('sort') as Sort) : 'followers';
  const nicheIds = useMemo(() => (params.get('niche') ?? '').split(',').filter(Boolean), [params]);
  const openHandle = params.get('c');

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete('c');
    setParams(next, { replace: true });
  };

  // Search box is local state, pushed to the URL after a pause.
  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    const id = setTimeout(() => { if (qInput.trim() !== q) setParam('q', qInput.trim() || null); }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const sz = SIZES.find((s) => s.key === size) ?? null;
  const listQ = useInfiniteQuery({
    queryKey: ['creator-directory', q, size, lang, priced, sort, nicheIds.join(',')],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await supabase.rpc('get_creator_directory', {
        p_q: q || null,
        p_niche_ids: nicheIds.length ? nicheIds : null,
        p_min_followers: sz?.min ?? null,
        p_max_followers: sz?.max ?? null,
        p_lang: lang || null,
        p_has_price: priced,
        p_sort: sort,
        p_limit: PAGE,
        p_offset: pageParam,
      });
      if (error) throw error;
      return (data ?? []) as DirRow[];
    },
    getNextPageParam: (last, all) => (last.length === PAGE ? all.length * PAGE : undefined),
  });
  const rows = useMemo(() => (listQ.data?.pages ?? []).flat(), [listQ.data]);
  const total = rows[0] ? Number(rows[0].total) : 0;

  const nichesQ = useSupabaseQuery<{ id: string; name_ru: string; name_en: string | null }[]>(
    ['niches-active'],
    async () => supabase.from('niches').select('id, name_ru, name_en').eq('is_active', true).order('name_ru'),
  );
  const nicheCountsQ = useSupabaseQuery<{ niche_id: string; blogger_count: number | string }[]>(
    ['niche-blogger-counts'],
    async () => supabase.rpc('get_niche_blogger_counts'),
  );
  const nicheCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of nicheCountsQ.data ?? []) m.set(r.niche_id, Number(r.blogger_count));
    return m;
  }, [nicheCountsQ.data]);
  const nicheName = (n: { name_ru: string; name_en: string | null }) => (currentLocale().startsWith('en') && n.name_en) || n.name_ru;
  const [nicheSheet, setNicheSheet] = useState(false);

  // Card: from the list row when we have it; for a shared link, look the handle up.
  const openRow = rows.find((r) => r.handle === openHandle) ?? null;
  const deepQ = useSupabaseQuery<DirRow[]>(
    ['creator-directory-one', openHandle],
    async () => supabase.rpc('get_creator_directory', { p_q: openHandle, p_limit: 5 }),
    { enabled: !!openHandle && !openRow },
  );
  const cardRow = openRow ?? (deepQ.data ?? []).find((r) => r.handle === openHandle) ?? null;

  const openCard = (r: DirRow) => {
    const next = new URLSearchParams(params);
    next.set('c', r.handle);
    setParams(next, { state: { cardPushed: true } });
  };
  const closeCard = () => {
    if ((location.state as { cardPushed?: boolean } | null)?.cardPushed) { navigate(-1); return; }
    const next = new URLSearchParams(params);
    next.delete('c');
    setParams(next, { replace: true });
  };

  const chip = (on: boolean): React.CSSProperties => ({
    flex: 'none', minHeight: 44, padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 'var(--fs-body)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
    background: on ? 'rgba(74,158,255,0.16)' : 'transparent', color: on ? '#bcd0ff' : '#c8cfdd',
    border: `1px solid ${on ? 'rgba(74,158,255,0.45)' : 'rgba(255,255,255,0.15)'}`,
  });
  const anyFilter = !!(q || size || lang || priced || nicheIds.length);

  return (
    <div className="cr-page" style={{ margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 12px', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>{t('creators.title')}</h1>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 46, padding: '0 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)' }}>
        <NamedIcon name="search" size={16} color="#889" />
        <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder={t('creators.searchPh')}
          aria-label={t('creators.searchPh')} type="search" autoComplete="off"
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontFamily: 'inherit', fontSize: 16 }} />
      </label>

      {/* Filters — one row that scrolls sideways on a phone. */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '10px 0', scrollbarWidth: 'none' }}>
        <button type="button" style={chip(nicheIds.length > 0)} onClick={() => setNicheSheet(true)}>
          {t('creators.f.niche')}{nicheIds.length > 0 ? ` · ${nicheIds.length}` : ''} <NamedIcon name="list" size={12} />
        </button>
        {SIZES.map((s) => (
          <button key={s.key} type="button" style={chip(size === s.key)} onClick={() => setParam('size', size === s.key ? null : s.key)}>{s.label}</button>
        ))}
        {LANGS.map((l) => (
          <button key={l} type="button" style={chip(lang === l)} onClick={() => setParam('lang', lang === l ? null : l)}>{t(`creators.lang.${l}`)}</button>
        ))}
        <button type="button" style={chip(priced)} onClick={() => setParam('price', priced ? null : '1')}>{t('creators.f.priced')}</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
          {listQ.isLoading ? '…' : t('creators.count', { count: total, n: total.toLocaleString(currentLocale()) })}
          {anyFilter && (
            <button type="button" onClick={() => { setQInput(''); setParams(new URLSearchParams(), { replace: true }); }}
              style={{ marginLeft: 10, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', font: 'inherit', padding: 0 }}>
              {t('creators.reset')}
            </button>
          )}
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
          {t('creators.sortBy')}
          <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'followers' ? null : e.target.value)}
            style={{ minHeight: 36, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: '#11151d', color: 'var(--text)', fontFamily: 'inherit', fontSize: 'var(--fs-body)', padding: '0 8px' }}>
            {SORTS.map((s) => <option key={s} value={s}>{t(`creators.sort.${s}`)}</option>)}
          </select>
        </label>
      </div>

      {listQ.isError ? (
        <div style={{ color: '#ff8f8f', padding: 24, textAlign: 'center' }}>{t('creators.error')}</div>
      ) : listQ.isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={68} />)}</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--muted)', padding: 32, textAlign: 'center' }}>{t('creators.empty')}</div>
      ) : (
        <div className="cr-list" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
          {/* Desktop only: the same rows as columns, sortable by clicking a header. */}
          <div className="cr-head" role="row">
            <span />
            <span>{t('creators.col.creator')}</span>
            {([['followers', t('creators.col.followers')], ['er', t('creators.col.er')], ['views', t('creators.col.views')], ['price', t('creators.col.price')]] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setParam('sort', k === 'followers' ? null : k)} aria-pressed={sort === k}
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left', color: sort === k ? 'var(--accent-text)' : 'var(--muted)' }}>
                {label}{sort === k ? ' ↓' : ''}
              </button>
            ))}
            <span>{t('creators.col.brands')}</span>
          </div>
          {rows.map((r) => {
            const er = r.er != null ? Number(r.er) : null;
            const erMed = r.er_median != null ? Number(r.er_median) : null;
            // Above the norm for the size → green. Too few posts → no ER at all («не измерено»).
            const erColor = !r.er_reliable || er == null || erMed == null ? undefined
              : er >= erMed * 1.2 ? 'var(--success-text)' : er < erMed * 0.7 ? 'var(--danger-text)' : undefined;
            const price = r.reel_price != null ? Number(r.reel_price) : null;
            return (
              <button key={r.provider_id} type="button" onClick={() => openCard(r)} className="cr-row"
                style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', minWidth: 0, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                  padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', color: 'var(--text)' }}>
                <Avatar src={r.avatar_url} name={r.handle} size={46} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--fs-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {r.full_name ?? `@${r.handle}`}
                    </span>
                    {r.verified && <VerifiedBadge verified size={14} />}
                    <span className="cr-m" style={{ marginLeft: 'auto', fontWeight: 600, fontVariantNumeric: 'tabular-nums', flex: 'none' }}>{compact(r.followers)}</span>
                  </div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[r.full_name ? `@${r.handle}` : null, (r.niches ?? []).slice(0, 2).join(', ') || null].filter(Boolean).join(' · ')}
                  </div>
                  <div className="cr-m" style={{ display: 'flex', gap: 12, fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 3, flexWrap: 'wrap' }}>
                    <span title={erMed != null ? t('creators.erNorm', { v: erMed.toLocaleString(currentLocale(), { maximumFractionDigits: 2 }) }) : undefined}>
                      ER {er != null
                        ? <span style={{ color: erColor }}>{`${er.toLocaleString(currentLocale(), { maximumFractionDigits: 2 })}%`}</span>
                        : <span style={{ color: 'var(--dim)' }}>{t('creators.erUnmeasured')}</span>}
                    </span>
                    {r.reel_views != null && <span><NamedIcon name="eye" size={11} color="#889" /> {compact(r.reel_views)}</span>}
                    <span style={{ color: price != null ? '#5fd17a' : 'var(--muted)' }}>
                      {price != null ? t('creators.reelPrice', { v: Math.round(price).toLocaleString(currentLocale()) }) : t('creators.noPrice')}
                    </span>
                    {!!r.brands_90d && <span>{t('creators.brands', { count: r.brands_90d })}</span>}
                  </div>
                </div>
                <span className="cr-d cr-num">{compact(r.followers)}</span>
                <span className="cr-d cr-num">
                  <span style={{ color: erColor }}>{er != null ? `${er.toLocaleString(currentLocale(), { maximumFractionDigits: 2 })}%` : '—'}</span>
                  {erMed != null && <span className="cr-sub">{r.er_reliable ? t('creators.erNormShort', { v: erMed.toLocaleString(currentLocale(), { maximumFractionDigits: 1 }) }) : t('creators.fewPosts')}</span>}
                </span>
                <span className="cr-d cr-num">{r.reel_views != null ? compact(r.reel_views) : '—'}</span>
                <span className="cr-d cr-num" style={{ color: price != null ? 'var(--success-text)' : 'var(--dim)' }}>
                  {price != null ? `${Math.round(price).toLocaleString(currentLocale())} ${t('creators.som')}` : t('creators.noPriceShort')}
                </span>
                <span className="cr-d cr-num">{r.brands_90d ?? 0}</span>
              </button>
            );
          })}
          {listQ.hasNextPage && (
            <button type="button" onClick={() => listQ.fetchNextPage()} disabled={listQ.isFetchingNextPage}
              style={{ ...chip(false), justifyContent: 'center', minHeight: 46, borderRadius: 12, marginTop: 4 }}>
              {listQ.isFetchingNextPage ? '…' : t('creators.more', { shown: rows.length, total })}
            </button>
          )}
        </div>
      )}

      {/* Niche picker — a sheet over the list, applies on Done. */}
      {nicheSheet && (
        <NicheSheet
          niches={(nichesQ.data ?? []).filter((n) => (nicheCount.get(n.id) ?? 0) > 0)}
          counts={nicheCount}
          label={nicheName}
          selected={nicheIds}
          onClose={() => setNicheSheet(false)}
          onApply={(ids) => { setNicheSheet(false); setParam('niche', ids.join(',') || null); }}
        />
      )}

      {openHandle && cardRow && (
        <Suspense fallback={null}>
          <CreatorPreviewModal
            handle={cardRow.handle}
            instagramProviderId={cardRow.provider_id}
            followers={cardRow.followers}
            engagement={cardRow.er != null ? Number(cardRow.er) : 0}
            onClose={closeCard}
          />
        </Suspense>
      )}
    </div>
  );
}

function NicheSheet({ niches, counts, label, selected, onClose, onApply }: {
  niches: { id: string; name_ru: string; name_en: string | null }[];
  counts: Map<string, number>;
  label: (n: { name_ru: string; name_en: string | null }) => string;
  selected: string[];
  onClose: () => void;
  onApply: (ids: string[]) => void;
}) {
  const { t } = useTranslation('customer');
  const [pick, setPick] = useState<Set<string>>(new Set(selected));
  const dialogRef = useDialogFocus<HTMLDivElement>();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const toggle = (id: string) => setPick((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div ref={dialogRef} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('creators.f.niche')}
        style={{ width: '100%', maxWidth: 560, maxHeight: '80dvh', display: 'flex', flexDirection: 'column', background: '#10141c', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div style={{ padding: '14px 16px 8px', fontWeight: 600, fontSize: 'var(--fs-h3, 16px)' }}>{t('creators.f.niche')}</div>
        <div style={{ overflowY: 'auto', padding: '0 8px' }}>
          {niches.map((n) => (
            <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 8px', borderRadius: 8, cursor: 'pointer', color: 'var(--text)' }}>
              <input type="checkbox" checked={pick.has(n.id)} onChange={() => toggle(n.id)} style={{ width: 18, height: 18 }} />
              <span style={{ flex: 1 }}>{label(n)}</span>
              <span style={{ color: 'var(--dim)', fontSize: 'var(--fs-caption)' }}>{counts.get(n.id) ?? 0}</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button type="button" onClick={() => setPick(new Set())}
            style={{ minHeight: 44, padding: '0 16px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer' }}>
            {t('creators.reset')}
          </button>
          <button type="button" onClick={() => onApply([...pick])}
            style={{ flex: 1, minHeight: 44, borderRadius: 10, background: '#2563eb', border: '1px solid #2563eb', color: '#fff', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
            {t('creators.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
